/**
 * Price Display Utilities
 * 
 * This module controls price visibility in customer-facing UI.
 * 
 * SHOW_PRICES: When false (default), all prices are hidden from customer UI.
 * Only admin/internal views should set this to true.
 * 
 * Internal pricing for calculations remains in:
 * - RequestWizard.jsx: PAINTING_PRICES, HANDYMAN_PRICES, etc. (used for estimate calculations)
 * - Backend: CREDIT_COSTS, lead pricing (used for contractor credit deductions)
 * 
 * To enable prices (admin mode):
 *   localStorage.setItem('SHOW_PRICES', 'true');
 *   window.location.reload();
 */

// Single source of truth for price visibility
export const SHOW_PRICES = localStorage.getItem('SHOW_PRICES') === 'true';

// Trust line to replace removed prices
export const PRICE_TRUST_LINE = "You'll get a clear quote before any work starts.";

/**
 * Sanitizes display text by removing pricing information.
 * 
 * Strips:
 * - Currency symbols ($, USD)
 * - Price ranges (e.g., "$75-$200", "1,800–3,800")
 * - "per X" units (per item, per set, per door, per sqft, /sqft, sq ft in price context)
 * - Price-related suffixes like "/door", "/sqft"
 * 
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text without pricing
 */
export function sanitizeDisplayText(text) {
  if (!text || typeof text !== 'string') return text;
  if (SHOW_PRICES) return text;
  
  const originalText = text;
  
  // Pattern to match price ranges with various formats:
  // $75–$200, $1,800–$3,800, $2.50–$4.00, etc.
  // Also matches: — $325–$650 each, — $1.75–$3.00/sqft
  const priceRangePattern = /\s*[—–-]\s*\$[\d,]+(?:\.[\d]+)?(?:\s*[—–-]\s*\$?[\d,]+(?:\.[\d]+)?)?(?:\s*(?:per\s+\w+|\/\w+|\s+each))?/gi;
  
  // Pattern for standalone prices: $50, $1,000, etc.
  const standalonePricePattern = /\$[\d,]+(?:\.[\d]+)?/g;
  
  // Pattern for "per X" units at end of string
  const perUnitPattern = /\s+per\s+(?:item|set|shelf|door|TV|sqft|sq\s*ft|unit|each)\s*$/gi;
  
  // Pattern for price suffixes like /sqft, /door
  const priceSuffixPattern = /\/(?:sqft|door|item|each)\s*$/gi;
  
  // Pattern for "(description required)" that often follows "Other" options
  const descriptionRequiredPattern = /\s*\(description required\)\s*$/gi;
  
  let sanitized = text;
  
  // Special handling: If text contains (+$X.XX–$Y.YY per sq ft labor) pattern, strip the whole parenthetical
  // This handles the flooring removal option: "Yes, remove existing flooring (+$1.50–$3.00 per sq ft labor)"
  sanitized = sanitized.replace(/\s*\(\+?\$[\d.,]+[—–-]\$?[\d.,]+\s*per\s+sq\s*ft\s*labor\)/gi, '');
  
  // Remove price ranges first (most specific)
  sanitized = sanitized.replace(priceRangePattern, '');
  
  // Remove any remaining standalone prices
  sanitized = sanitized.replace(standalonePricePattern, '');
  
  // Remove "per X" suffixes
  sanitized = sanitized.replace(perUnitPattern, '');
  
  // Remove price suffixes
  sanitized = sanitized.replace(priceSuffixPattern, '');
  
  // Keep "(description required)" - it's useful context
  // sanitized = sanitized.replace(descriptionRequiredPattern, '');
  
  // Clean up any double spaces or trailing dashes/spaces
  sanitized = sanitized.replace(/\s+/g, ' ').trim();
  sanitized = sanitized.replace(/\s*[—–-]\s*$/, '').trim();
  
  // Dev warning if sanitization occurred
  if (process.env.NODE_ENV === 'development' && sanitized !== originalText) {
  }
  
  return sanitized;
}

/**
 * Sanitizes an array of option objects, removing prices from labels.
 * 
 * @param {Array} options - Array of {value, label} objects
 * @returns {Array} - Options with sanitized labels
 */
export function sanitizeOptions(options) {
  if (!Array.isArray(options)) return options;
  if (SHOW_PRICES) return options;
  
  return options.map(opt => ({
    ...opt,
    label: sanitizeDisplayText(opt.label)
  }));
}

/**
 * Check if text contains pricing patterns (for build/CI checks)
 * 
 * @param {string} text - Text to check
 * @returns {boolean} - True if pricing patterns found
 */
export function containsPricing(text) {
  if (!text || typeof text !== 'string') return false;
  
  const patterns = [
    /\$[\d,]+/,           // Dollar amounts
    /USD\s*[\d,]+/i,      // USD amounts
    /[\d,]+\s*\/\s*sqft/i, // per sqft
    /per\s+(?:item|set|shelf|door|TV|sqft|sq\s*ft|unit|each)/i, // per unit
  ];
  
  return patterns.some(pattern => pattern.test(text));
}

/**
 * Runtime guard - logs warning if component tries to render pricing
 * Call this in useEffect for customer-facing components
 * 
 * @param {string} componentName - Name of the component for logging
 * @param {object} props - Props to check for pricing
 */
export function guardAgainstPriceDisplay(componentName, props) {
  if (SHOW_PRICES) return;
  if (process.env.NODE_ENV !== 'development') return;
  
  const checkValue = (key, value) => {
    if (typeof value === 'string' && containsPricing(value)) {
    }
  };
  
  Object.entries(props || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        if (typeof item === 'object' && item.label) {
          if (containsPricing(item.label)) {
          }
        }
      });
    } else {
      checkValue(key, value);
    }
  });
}

export default {
  SHOW_PRICES,
  PRICE_TRUST_LINE,
  sanitizeDisplayText,
  sanitizeOptions,
  containsPricing,
  guardAgainstPriceDisplay
};
